import React, { useState } from "react";
import { Alert, Badge, Col, Modal, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import moment from "moment";
import { Input } from "../../components";
import {
  bacaMasalah,
  simpanBaseline,
} from "../../config/redux/action/sustainabilityAction";
import { formatAngka } from "../../utils";

const BASELINE_TYPE = [
  { id: "conventional_home", name: "Conventional Home (cuci rumah tangga)" },
  { id: "conventional_commercial", name: "Conventional Commercial (laundry konvensional)" },
  { id: "internal_historical", name: "Internal Historical (data historis sendiri)" },
];

const TIER = [
  { id: "T1", name: "T1 (terukur, terverifikasi pihak ketiga)" },
  { id: "T2", name: "T2 (terukur internal)" },
  { id: "T3", name: "T3 (literatur spesifik)" },
  { id: "T4", name: "T4 (estimasi kasar)" },
];

const kosong = {
  indicator_code: "",
  baseline_type: "conventional_home",
  nilai: "",
  unit: "",
  sumber: "",
  metodologi: "",
  catatan_keterbatasan: "",
  quality_tier: "T4",
  berlaku_dari: new Date().toISOString().slice(0, 10),
  berlaku_sampai: "",
};

function TabBaseline({ list, indikatorList, update }) {
  const [Show, setShow] = useState(false);
  const [Form, setForm] = useState(kosong);
  const [Error, setError] = useState([]);
  const dispatch = useDispatch();

  const ubah = (key, value) => setForm({ ...Form, [key]: value });

  const buka = () => {
    setForm(kosong);
    setError([]);
    setShow(true);
  };

  const simpan = () => {
    setError([]);
    dispatch(
      simpanBaseline({
        indicator_code: Form.indicator_code,
        baseline_type: Form.baseline_type,
        nilai: Form.nilai === "" ? undefined : Number(Form.nilai),
        unit: Form.unit,
        sumber: Form.sumber,
        metodologi: Form.metodologi,
        catatan_keterbatasan: Form.catatan_keterbatasan || null,
        quality_tier: Form.quality_tier,
        berlaku_dari: Form.berlaku_dari || undefined,
        berlaku_sampai: Form.berlaku_sampai || null,
      })
    )
      .then(() => {
        update();
        setShow(false);
      })
      .catch((err) => setError(bacaMasalah(err)));
  };

  const dataKode = [
    { id: "", name: "— pilih kode indikator —" },
    ...(indikatorList || [])
      .filter(
        (val, i, arr) => arr.findIndex((x) => x.code === val.code) === i
      )
      .map((val) => ({ id: val.code, name: `${val.code} — ${val.name_id}` })),
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="text-muted" style={{ fontSize: "11px" }}>
          Setiap penyimpanan membuat versi baru dan mengarsipkan baseline aktif
          sebelumnya untuk kode yang sama.
        </div>
        <button className="btn btn-sm btn-primary" onClick={buka}>
          Tambah Baseline
        </button>
      </div>

      <div className="table-responsive">
        <table
          className="table table-bordered align-middle mb-0"
          style={{ fontSize: "12px" }}
        >
          <thead className="table-light">
            <tr>
              <th>Indikator</th>
              <th>Tipe</th>
              <th className="text-end">Nilai</th>
              <th>Unit</th>
              <th className="text-center">Tier</th>
              <th>Sumber</th>
              <th>Metodologi</th>
              <th>Berlaku</th>
              <th className="text-center">Versi</th>
              <th className="text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {list && list.length > 0 ? (
              list.map((val) => (
                <tr key={val.id}>
                  <td className="fw-bold">{val.indicator_code}</td>
                  <td>{val.baseline_type}</td>
                  <td className="text-end">{formatAngka(val.nilai, 4)}</td>
                  <td>{val.unit}</td>
                  <td className="text-center">
                    <Badge bg="secondary">{val.quality_tier}</Badge>
                  </td>
                  <td className="ws-preline">
                    {val.sumber || (
                      <span className="text-danger fw-bold">tidak ada sumber</span>
                    )}
                  </td>
                  <td className="ws-preline">{val.metodologi || "-"}</td>
                  <td>
                    {val.berlaku_dari
                      ? moment(val.berlaku_dari).format("DD MMM YYYY")
                      : "-"}
                    {val.berlaku_sampai
                      ? ` s/d ${moment(val.berlaku_sampai).format("DD MMM YYYY")}`
                      : ""}
                  </td>
                  <td className="text-center">v{val.version}</td>
                  <td className="text-center">
                    <Badge bg={val.status === "active" ? "success" : "dark"}>
                      {val.status}
                    </Badge>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={10} align="center">
                  Data tidak ditemukan
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal show={Show} onHide={() => setShow(false)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "16px" }}>Tambah Baseline</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Backend menolak baseline tanpa sumber dan metodologi, dan pesannya
              datang sebagai data.masalah berisi beberapa poin sekaligus. */}
          {Error.length > 0 && (
            <Alert variant="danger" style={{ fontSize: "12px" }}>
              <div className="fw-bold mb-1">Baseline belum bisa disimpan</div>
              <ul className="mb-0 ps-3">
                {Error.map((val, i) => (
                  <li key={i}>{val}</li>
                ))}
              </ul>
            </Alert>
          )}

          <Alert variant="info" style={{ fontSize: "11px" }}>
            Baseline adalah pembanding yang dipakai untuk seluruh klaim penghematan.
            Tanpa <b>sumber</b> dan <b>metodologi</b> yang jelas, angka penghematan
            tidak dapat dipertanggungjawabkan, karena itu keduanya wajib diisi.
          </Alert>

          <Row>
            <Col xs={12} lg={6}>
              <Input
                judul="Kode Indikator *"
                type="select"
                data={dataKode}
                value={Form.indicator_code}
                onChange={(e) => ubah("indicator_code", e.target.value)}
              />
              <Input
                judul="Tipe Baseline *"
                type="select"
                data={BASELINE_TYPE}
                value={Form.baseline_type}
                onChange={(e) => ubah("baseline_type", e.target.value)}
              />
              <Input
                judul="Nilai *"
                type="number"
                value={Form.nilai}
                onChange={(e) => ubah("nilai", e.target.value)}
              />
              <Input
                judul="Unit *"
                type="text"
                placeholder="liter/kg, kWh/kg"
                value={Form.unit}
                onChange={(e) => ubah("unit", e.target.value)}
              />
              <Input
                judul="Quality Tier"
                type="select"
                data={TIER}
                value={Form.quality_tier}
                onChange={(e) => ubah("quality_tier", e.target.value)}
              />
            </Col>
            <Col xs={12} lg={6}>
              <Input
                judul="Sumber * (wajib)"
                type="textarea"
                placeholder="Judul studi, lembaga, tahun, atau tautan data internal"
                value={Form.sumber}
                onChange={(e) => ubah("sumber", e.target.value)}
              />
              <Input
                judul="Metodologi * (wajib)"
                type="textarea"
                placeholder="Bagaimana angka ini diperoleh dan pada kondisi apa berlaku"
                value={Form.metodologi}
                onChange={(e) => ubah("metodologi", e.target.value)}
              />
              <Input
                judul="Catatan Keterbatasan"
                type="textarea"
                placeholder="Kondisi di mana baseline ini tidak berlaku"
                value={Form.catatan_keterbatasan}
                onChange={(e) => ubah("catatan_keterbatasan", e.target.value)}
              />
              <Input
                judul="Berlaku Dari"
                type="date"
                value={Form.berlaku_dari}
                onChange={(e) => ubah("berlaku_dari", e.target.value)}
              />
              <Input
                judul="Berlaku Sampai"
                type="date"
                value={Form.berlaku_sampai}
                onChange={(e) => ubah("berlaku_sampai", e.target.value)}
              />
            </Col>
          </Row>

          <div className="d-flex justify-content-end">
            <button
              className="btn btn-sm btn-secondary me-2"
              onClick={() => setShow(false)}
            >
              Batal
            </button>
            <button className="btn btn-sm btn-primary" onClick={simpan}>
              Simpan Versi Baru
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default TabBaseline;
